import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  LayoutDashboard,
  FolderKanban,
  Settings,
  FileText,
  Database,
  LogOut,
  CalendarDays,
  Package,
  BarChart3,
} from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import SettingsPage from './pages/Settings';
import Logs from './pages/Logs';
import Schedules from './pages/Schedules';
import PluginManagement from './pages/PluginManagement';
import Reports from './pages/Reports';

const queryClient = new QueryClient();

function Navigation() {
  const { logout } = useAuth();

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/projects', icon: FolderKanban, label: 'Projekte' },
    { to: '/schedules', icon: CalendarDays, label: 'Zeitpläne' },
    { to: '/plugins', icon: Package, label: 'Plugin Updates' },
    { to: '/reports', icon: BarChart3, label: 'Berichte' },
    { to: '/settings', icon: Settings, label: 'Einstellungen' },
    { to: '/logs', icon: FileText, label: 'Logs' },
  ];

  return (
    <nav className="fixed left-0 top-0 h-full w-64 bg-gray-900 text-white p-4 flex flex-col">
      <div className="flex items-center gap-3 mb-8 px-2">
        <Database className="w-8 h-8 text-primary-400" />
        <span className="text-xl font-bold">BackupPilot</span>
      </div>
      <ul className="space-y-1 flex-1">
        {navItems.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
      <button
        onClick={logout}
        className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors mt-auto"
      >
        <LogOut className="w-5 h-5" />
        Abmelden
      </button>
    </nav>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <main className="ml-64 p-8">{children}</main>
    </div>
  );
}

function AuthenticatedApp() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/schedules" element={<Schedules />} />
          <Route path="/plugins" element={<PluginManagement />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/logs" element={<Logs />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthenticatedApp />
      </AuthProvider>
    </QueryClientProvider>
  );
}
